import { IconNames } from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import { BitmapText, Graphics, MSAA_QUALITY, Sprite, Texture } from 'pixi.js';
import { useEffect, useState } from 'react';

import type { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { useEditorContext } from 'flow-builder/editor.context';
import { HighlightType } from 'flow-builder/types/node';

import { textStyles } from '../../node/textStyles';
import { neutral } from '../common/colors';
import { styles as commonStyles } from '../common/commonStyles';
import { getIconTextures } from '../utils/icons';

import { fallbackNode } from './nodeRenderer';
import { styles } from './nodeStyles';

export type NodeComponentBaseTextureMap = Record<
  FlowNodeTypes | 'fallback',
  Texture
>;

export type NodePortTextureMap = Record<HighlightType, Texture>;

const textureGenerationSettings = {
  resolution: window.devicePixelRatio * 2,
  multisample: MSAA_QUALITY.HIGH,
};

/*
 * Node component base is used to create the re-usable part of a node as a texture.
 * This speeds up performance as it's possible to quickly duplicate it.
 */
export const useNodeComponentBaseTextures =
  (): NodeComponentBaseTextureMap | null => {
    const {
      view,
      props: { flow },
    } = useEditorContext();
    const [nodeTextures, setNodeTextures] =
      useState<NodeComponentBaseTextureMap | null>(null);

    useEffect(() => {
      if (!view.isReady) {
        return;
      }

      const attributesWithFallback = {
        ...flow.attributes,
        fallback: fallbackNode,
      };

      const icons = Object.values(flow.attributes).map<IconNames>(
        ({ iconName }) => iconName,
      );

      const iconTextures = Promise.all([
        getIconTextures(icons),
        getIconTextures(['pen'], { fill: typography.color.secondary }),
      ]).then(([nodeTypeIcons, editIcon]) => ({
        ...nodeTypeIcons,
        ...editIcon,
      }));

      iconTextures.then((iconTextureMap) => {
        const nodeTextures = Object.entries(
          attributesWithFallback,
        ).reduce<NodeComponentBaseTextureMap>((map, [name, nodeAttributes]) => {
          const hasActionModal = Reflect.has(nodeAttributes, 'ActionModal');
          const {
            isEditable = hasActionModal,
            attributes,
            iconName,
            label,
            color,
          } = nodeAttributes;

          const head = new Graphics();

          const { width: headWidth, height: headHeight } = styles.head;
          const { cornerRadius: radius } = styles.base;

          head.beginFill(neutral['100']);
          head
            .moveTo(styles.base.cornerRadius, 0)
            .arcTo(headWidth, 0, headWidth, 0 + radius, radius)
            .lineTo(headWidth, 0 + radius)
            .lineTo(headWidth, headHeight)
            .lineTo(0, headHeight)
            .arcTo(0, 0, radius, 0, radius);
          head.endFill();

          const typeIndicator = new Graphics();
          const icon = new Sprite(iconTextureMap[iconName]);
          const header = new BitmapText(label, textStyles.semibold);
          const editIcon = new Sprite(iconTextureMap['pen']);

          icon.height = styles._typeIcon.size;
          icon.width = styles._typeIcon.size;
          icon.anchor.set(styles._typeIcon.anchor.x, styles._typeIcon.anchor.y);
          icon.position.set(
            styles._typeIcon.position.x,
            styles._typeIcon.position.y,
          );

          editIcon.height = styles._editIcon.size;
          editIcon.width = styles._editIcon.size;
          editIcon.anchor.set(
            styles._editIcon.anchor.x,
            styles._editIcon.anchor.y,
          );
          editIcon.position.set(
            styles._editIcon.position.x,
            styles._editIcon.position.y,
          );

          typeIndicator.lineStyle(styles._typeIndicator.size, color);
          typeIndicator.lineTo(0, styles._typeIndicator.length);
          typeIndicator.line.cap = styles._typeIndicator.cap;
          typeIndicator.position.set(
            styles._typeIndicator.position.x,
            styles._typeIndicator.position.y,
          );

          header.anchor.set(0, 0.5);
          header.position.set(
            styles._headText.position.x,
            (styles.head.height + textStyles.bitmapTextOffset) / 2,
          );

          head.addChild(typeIndicator);
          head.addChild(header);
          head.addChild(icon);

          if (attributes?.isDeprecated) {
            const pill = new Graphics();
            const text = new BitmapText(
              'Update available',
              styles._deprecatedText,
            );

            if (typeof attributes.label === 'string') {
              header.text = attributes.label;
            }

            text.anchor.set(0, 0.5);
            text.position.set(styles._deprecatedPill.padding, 7);

            pill.beginFill(styles._deprecatedPill.fill);
            pill.drawRoundedRect(
              0,
              0,
              text.textWidth + styles._deprecatedPill.padding * 2,
              styles._deprecatedPill.height,
              styles._deprecatedPill.height / 2,
            );
            pill.endFill();

            pill.position.set(
              styles._deprecatedPill.position.x,
              styles._deprecatedPill.position.y,
            );

            pill.addChild(text);
            head.addChild(pill);
          }

          if (isEditable) {
            head.addChild(editIcon);
          }

          map[name] = view.app.renderer.generateTexture(
            head,
            textureGenerationSettings,
          );

          return map;
        }, {} as NodeComponentBaseTextureMap);

        setNodeTextures(nodeTextures);
      });
    }, [view.isReady, view.app, flow.attributes]);

    return nodeTextures;
  };

export const useNodePortTextures = (): NodePortTextureMap | null => {
  const { view } = useEditorContext();
  const [portTexture, setPortTexture] = useState<NodePortTextureMap | null>(
    null,
  );

  useEffect(() => {
    if (!view.isReady) {
      return;
    }

    const drawPort = (color: number) => {
      const circle = new Graphics();

      circle.lineStyle(styles._port.width, color);
      circle.beginFill(styles._port.fill);
      circle.drawCircle(0, 0, styles._port.radius);
      circle.endFill();

      return circle;
    };

    const generateHighlightTexture = (color: number = styles._port.border) =>
      view.app.renderer.generateTexture(
        drawPort(color),
        textureGenerationSettings,
      );

    const textureMap = {
      [HighlightType.NONE]: generateHighlightTexture(),
      [HighlightType.INFO]: generateHighlightTexture(
        commonStyles.highlight[HighlightType.INFO],
      ),
      [HighlightType.ERROR]: generateHighlightTexture(
        commonStyles.highlight[HighlightType.ERROR],
      ),
    };

    setPortTexture(textureMap);
  }, [view.isReady, view.app]);

  return portTexture;
};
